import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type {
  CreditWallet,
  CreditPackage,
  CreditActionType,
  CreditActionCost,
  CreditLogItem,
} from '../types/credits';

interface CreditState {
  wallet: CreditWallet | null;
  balance: number | null;
  packages: CreditPackage[];
  actionCosts: Record<CreditActionType, number>;
  transactionHistory: CreditLogItem[];

  // Specific loading states
  loadingBalance: boolean;
  loadingPackages: boolean;
  loadingActionCosts: boolean;
  loadingCheckout: boolean;
  loadingHistory: boolean;

  error: string | null;
  lastSyncedAt: string | null;

  fetchPackages: () => Promise<void>;
  fetchActionCosts: () => Promise<void>;
  getActionCost: (actionType: CreditActionType) => number;
  createCheckoutSession: (
    productId: string,
    priceId: string,
    returnUrl?: string
  ) => Promise<string>;
  refreshBalance: () => Promise<void>;
  fetchLatestCreditsLogs: (limit?: number) => Promise<void>;
}

export const useCreditStore = create<CreditState>((set, get) => ({
  wallet: null,
  balance: null,
  packages: [],
  transactionHistory: [],
  actionCosts: {} as Record<CreditActionType, number>,

  // Initialize all loading states
  loadingBalance: false,
  loadingPackages: false,
  loadingHistory: false,
  loadingActionCosts: false,
  loadingCheckout: false,

  error: null,
  lastSyncedAt: null,

  fetchPackages: async () => {
    if (get().loadingPackages) return;
    set({ loadingPackages: true, error: null });
    try {
      const { data, error } = await supabase
        .from('credit_packages')
        .select('*')
        .eq('is_active', true)
        .order('credits', { ascending: true });

      if (error) throw error;

      // Get the most recent sync time
      const lastSynced =
        data && data.length > 0
          ? data.reduce((latest, pkg) => {
              const pkgTime = pkg.last_synced_at ? new Date(pkg.last_synced_at).getTime() : 0;
              return pkgTime > latest ? pkgTime : latest;
            }, 0)
          : null;

      set({
        packages: data || [],
        lastSyncedAt: lastSynced ? new Date(lastSynced).toISOString() : null,
        loadingPackages: false,
      });
    } catch (error) {
      set({
        error: (error as Error).message,
        loadingPackages: false,
      });
      console.error('Error fetching packages:', error);
    }
  },

  fetchActionCosts: async () => {
    if (get().loadingActionCosts) return;
    set({ loadingActionCosts: true });
    try {
      const { data, error } = await supabase.rpc('get_credit_action_costs');

      if (error) throw error;

      // Convert array to Record for easy lookup
      const costsMap = (data as CreditActionCost[]).reduce(
        (acc, item) => {
          acc[item.action_type] = item.cost;
          return acc;
        },
        {} as Record<CreditActionType, number>
      );

      set({ actionCosts: costsMap, loadingActionCosts: false });
    } catch (error) {
      console.error('Error fetching action costs:', error);
      set({ loadingActionCosts: false });
    }
  },

  getActionCost: (actionType: CreditActionType) => {
    const costs = get().actionCosts;
    return costs[actionType] ?? 0;
  },

  createCheckoutSession: async (productId: string, priceId: string, returnUrl?: string) => {
    set({ loadingCheckout: true, error: null });
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Use returnUrl if provided, otherwise default to dashboard
      const baseUrl = returnUrl || '/dashboard';
      const successUrl = `${window.location.origin}${baseUrl}${baseUrl.includes('?') ? '&' : '?'}purchase=success`;
      const cancelUrl = `${window.location.origin}${baseUrl}${baseUrl.includes('?') ? '&' : '?'}purchase=cancelled`;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-payment`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            productId,
            priceId,
            successUrl,
            cancelUrl,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      set({ loadingCheckout: false });
      return url;
    } catch (error) {
      set({
        error: (error as Error).message,
        loadingCheckout: false,
      });
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  refreshBalance: async () => {
    try {
      if (get().loadingBalance) return;
      set({ loadingBalance: true, error: null });
      const { data, error } = await supabase.rpc('get_credit_balance');

      if (error) throw error;

      set({ balance: data || 0 });
    } catch (error) {
      set({ error: (error as Error).message, loadingBalance: false });
      console.error('Error refreshing balance:', error);
    } finally {
      set({ loadingBalance: false });
    }
  },

  fetchLatestCreditsLogs: async (limit: number = 10) => {
    if (get().loadingHistory) return;
    set({ loadingHistory: true, error: null });
    try {
      const { data, error } = await supabase.rpc('get_latest_credits_logs', {
        p_limit: limit,
      });

      if (error) throw error;

      set({ transactionHistory: (data as CreditLogItem[]) || [], loadingHistory: false });
    } catch (error) {
      set({
        error: (error as Error).message,
        loadingHistory: false,
      });
      console.error('Error fetching credit logs:', error);
    }
  },
}));
